import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Zap, 
  ArrowLeft, 
  RefreshCw, 
  Trash2, 
  Settings, 
  CheckCircle, 
  XCircle, 
  AlertCircle,
  Loader2,
  Clock,
  ExternalLink,
  Activity,
  List,
  BarChart3
} from 'lucide-react';
import { 
  getIntegrationById, 
  getIntegrationProvider, 
  deleteIntegration, 
  syncIntegrationData,
  IntegrationProvider,
  Integration
} from '../lib/integrations';

const IntegrationDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [integration, setIntegration] = useState<Integration | null>(null);
  const [provider, setProvider] = useState<IntegrationProvider | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [syncHistory, setSyncHistory] = useState<any[]>([]);
  const [connectedItems, setConnectedItems] = useState<any[]>([]);

  useEffect(() => {
    fetchIntegration();
  }, [id]);

  const fetchIntegration = async () => {
    if (!id) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const data = await getIntegrationById(id);
      
      if (!data) {
        setError('Integration not found');
        return;
      }
      
      setIntegration(data);
      
      const providerDetails = getIntegrationProvider(data.provider);
      setProvider(providerDetails);
      
      // Simulate fetching sync history
      setSyncHistory([
        { id: 1, timestamp: new Date(Date.now() - 86400000).toISOString(), status: 'success', items: 42, duration: '1m 23s' },
        { id: 2, timestamp: new Date(Date.now() - 172800000).toISOString(), status: 'success', items: 38, duration: '1m 12s' },
        { id: 3, timestamp: new Date(Date.now() - 259200000).toISOString(), status: 'error', items: 0, duration: '0m 45s', error: 'API rate limit exceeded' },
        { id: 4, timestamp: new Date(Date.now() - 345600000).toISOString(), status: 'success', items: 35, duration: '1m 05s' },
      ]);
      
      // Simulate fetching connected items
      setConnectedItems([
        { id: 'ADAPT-1234', type: 'backlog', name: 'Implement AI-Powered Personalisation Engine', externalId: 'JIRA-456', status: 'in-progress' },
        { id: 'ADAPT-1235', type: 'backlog', name: 'Mobile Checkout Flow Redesign', externalId: 'JIRA-457', status: 'review' },
        { id: 'EXP-001', type: 'experiment', name: 'AI-Powered Personalisation', externalId: 'JIRA-458', status: 'running' },
        { id: 'EXP-002', type: 'experiment', name: 'Mobile-First Checkout', externalId: 'JIRA-459', status: 'completed' },
      ]);
    } catch (err) {
      console.error('Error fetching integration:', err);
      setError('Failed to load integration');
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async () => {
    if (!id) return;
    
    try {
      setSyncing(true);
      setError(null);
      
      const result = await syncIntegrationData(id);
      
      if (result.success) {
        // Add new sync record to history
        setSyncHistory(prev => [
          { 
            id: Date.now(), 
            timestamp: new Date().toISOString(), 
            status: 'success', 
            items: Math.floor(Math.random() * 10) + 35, 
            duration: `${Math.floor(Math.random() * 2) + 1}m ${Math.floor(Math.random() * 60)}s` 
          },
          ...prev
        ]);
        
        // Refresh integration data
        fetchIntegration();
      } else {
        setError(result.message);
      }
    } catch (err) {
      console.error('Error syncing integration:', err);
      setError('Failed to sync integration data');
    } finally {
      setSyncing(false);
    }
  };

  const handleDelete = async () => {
    if (!id) return;
    
    if (!confirm('Are you sure you want to delete this integration? This action cannot be undone.')) {
      return;
    }
    
    try {
      setLoading(true);
      
      const success = await deleteIntegration(id);
      
      if (success) {
        navigate('/integrations');
      } else {
        setError('Failed to delete integration');
      }
    } catch (err) {
      console.error('Error deleting integration:', err);
      setError('Failed to delete integration');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return (
          <div className="flex items-center space-x-1 px-2 py-1 bg-green-900 text-green-300 rounded-full text-xs">
            <CheckCircle className="w-3 h-3" />
            <span>Active</span>
          </div>
        );
      case 'inactive':
        return (
          <div className="flex items-center space-x-1 px-2 py-1 bg-gray-700 text-gray-300 rounded-full text-xs">
            <XCircle className="w-3 h-3" />
            <span>Inactive</span>
          </div>
        );
      case 'error':
        return (
          <div className="flex items-center space-x-1 px-2 py-1 bg-red-900 text-red-300 rounded-full text-xs">
            <AlertCircle className="w-3 h-3" />
            <span>Error</span>
          </div>
        );
      case 'success':
        return (
          <div className="flex items-center space-x-1 px-2 py-1 bg-green-900 text-green-300 rounded-full text-xs">
            <CheckCircle className="w-3 h-3" />
            <span>Success</span>
          </div>
        );
      default:
        return (
          <div className="flex items-center space-x-1 px-2 py-1 bg-gray-700 text-gray-300 rounded-full text-xs">
            <span>Unknown</span>
          </div>
        );
    }
  };

  const getItemTypeBadge = (type: string) => {
    switch (type) {
      case 'backlog':
        return (
          <div className="flex items-center space-x-1 px-2 py-1 bg-blue-900 text-blue-300 rounded-full text-xs">
            <List className="w-3 h-3" />
            <span>Backlog</span>
          </div>
        );
      case 'experiment':
        return (
          <div className="flex items-center space-x-1 px-2 py-1 bg-purple-900 text-purple-300 rounded-full text-xs">
            <Activity className="w-3 h-3" />
            <span>Experiment</span>
          </div>
        );
      default:
        return (
          <div className="flex items-center space-x-1 px-2 py-1 bg-gray-700 text-gray-300 rounded-full text-xs">
            <span>Unknown</span>
          </div>
        );
    }
  };

  if (loading && !integration) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 text-indigo-500 animate-spin mx-auto mb-4" />
          <p className="text-gray-400">Loading integration...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-4">
        <button
          onClick={() => navigate('/integrations')}
          className="p-2 bg-gray-800 rounded-lg text-gray-400 hover:text-white transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1">
          <div className="flex items-center space-x-3">
            <h1 className="text-2xl lg:text-3xl font-bold text-white">
              {integration?.name || 'Integration Details'}
            </h1>
            {integration && getStatusBadge(integration.status)}
          </div>
          <p className="text-gray-400 text-sm lg:text-base">
            {provider?.description || 'Integration details and configuration'}
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => navigate(`/integrations/edit/${id}`)}
            className="p-2 bg-gray-700 hover:bg-gray-600 text-gray-300 hover:text-white rounded-lg transition-colors"
          >
            <Settings className="w-5 h-5" />
          </button>
          <button
            onClick={handleDelete}
            className="p-2 bg-red-900 hover:bg-red-800 text-red-300 hover:text-white rounded-lg transition-colors"
          >
            <Trash2 className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div className="bg-red-900 border border-red-700 rounded-lg p-4 flex items-center">
          <AlertCircle className="w-5 h-5 text-red-400 mr-2 flex-shrink-0" />
          <span className="text-red-300">{error}</span>
        </div>
      )}

      {/* Integration Overview */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Integration Info */}
        <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
          <h2 className="text-lg font-semibold text-white mb-4">Integration Info</h2>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm text-gray-400 mb-1">Provider</label>
              <div className="flex items-center space-x-2">
                {provider?.logo ? (
                  <img 
                    src={provider.logo} 
                    alt={provider.name} 
                    className="w-6 h-6 object-contain bg-white p-0.5 rounded"
                  />
                ) : (
                  <Zap className="w-5 h-5 text-indigo-400" />
                )}
                <span className="text-white font-medium">{provider?.name || integration?.provider}</span>
              </div>
            </div>
            
            <div>
              <label className="block text-sm text-gray-400 mb-1">Status</label>
              {integration && getStatusBadge(integration.status)}
            </div>
            
            <div>
              <label className="block text-sm text-gray-400 mb-1">Last Synced</label>
              <div className="text-white">
                {integration?.last_sync 
                  ? new Date(integration.last_sync).toLocaleString()
                  : 'Never'}
              </div>
            </div>
            
            <div>
              <label className="block text-sm text-gray-400 mb-1">Created</label>
              <div className="text-white">
                {integration?.created_at 
                  ? new Date(integration.created_at).toLocaleString()
                  : 'Unknown'}
              </div>
            </div>
            
            <div className="pt-4">
              <button
                onClick={handleSync}
                disabled={syncing}
                className="w-full py-2 px-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {syncing ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Syncing...</span>
                  </>
                ) : (
                  <>
                    <RefreshCw className="w-4 h-4" />
                    <span>Sync Now</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Configuration Summary */}
        <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-white">Configuration</h2>
            <button
              onClick={() => navigate(`/integrations/edit/${id}`)}
              className="text-xs text-indigo-400 hover:text-indigo-300 flex items-center"
            >
              <span>Edit</span>
              <Settings className="w-3 h-3 ml-1" />
            </button>
          </div>
          
          <div className="space-y-4">
            {provider?.configFields.map((field) => (
              <div key={field.name}>
                <label className="block text-sm text-gray-400 mb-1">{field.label}</label>
                <div className="text-white break-all">
                  {field.type === 'password' ? (
                    '••••••••••••'
                  ) : field.type === 'checkbox' ? (
                    integration?.config?.[field.name] ? 'Yes' : 'No'
                  ) : (
                    integration?.config?.[field.name] || 'Not set'
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Sync Statistics */}
        <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
          <h2 className="text-lg font-semibold text-white mb-4">Sync Statistics</h2>
          
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className="bg-gray-700 rounded-lg p-4 text-center">
              <div className="text-xs text-gray-400 mb-1">Total Syncs</div>
              <div className="text-2xl font-bold text-white">{syncHistory.length}</div>
            </div>
            <div className="bg-gray-700 rounded-lg p-4 text-center">
              <div className="text-xs text-gray-400 mb-1">Success Rate</div>
              <div className="text-2xl font-bold text-green-400">
                {syncHistory.length > 0 
                  ? `${Math.round((syncHistory.filter(s => s.status === 'success').length / syncHistory.length) * 100)}%`
                  : '0%'}
              </div>
            </div>
            <div className="bg-gray-700 rounded-lg p-4 text-center">
              <div className="text-xs text-gray-400 mb-1">Items Synced</div>
              <div className="text-2xl font-bold text-white">
                {syncHistory.reduce((sum, sync) => sum + (sync.items || 0), 0)}
              </div>
            </div>
            <div className="bg-gray-700 rounded-lg p-4 text-center">
              <div className="text-xs text-gray-400 mb-1">Last Sync</div>
              <div className="text-lg font-bold text-white">
                {syncHistory.length > 0 
                  ? new Date(syncHistory[0].timestamp).toLocaleDateString()
                  : 'Never'}
              </div>
            </div>
          </div>
          
          <div>
            <h3 className="text-sm font-medium text-gray-300 mb-2">Sync Activity</h3>
            <div className="space-y-2">
              {syncHistory.slice(0, 3).map((sync) => (
                <div key={sync.id} className="flex items-center justify-between p-2 bg-gray-700 rounded-lg">
                  <div className="flex items-center space-x-2">
                    {sync.status === 'success' ? (
                      <CheckCircle className="w-4 h-4 text-green-400" />
                    ) : (
                      <XCircle className="w-4 h-4 text-red-400" />
                    )}
                    <span className="text-sm text-white">
                      {sync.items} items in {sync.duration}
                    </span>
                  </div>
                  <div className="text-xs text-gray-400">
                    {new Date(sync.timestamp).toLocaleString()}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Connected Items */}
      <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
        <h2 className="text-lg font-semibold text-white mb-4">Connected Items</h2>
        
        {connectedItems.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-left text-xs text-gray-400 border-b border-gray-700">
                  <th className="pb-2 font-medium">ADAPT ID</th>
                  <th className="pb-2 font-medium">Type</th>
                  <th className="pb-2 font-medium">Name</th>
                  <th className="pb-2 font-medium">External ID</th>
                  <th className="pb-2 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {connectedItems.map((item) => (
                  <tr key={item.id} className="border-b border-gray-700">
                    <td className="py-3 text-white">{item.id}</td>
                    <td className="py-3">{getItemTypeBadge(item.type)}</td>
                    <td className="py-3 text-white">{item.name}</td>
                    <td className="py-3">
                      <a href="#" className="text-indigo-400 hover:text-indigo-300 flex items-center space-x-1">
                        <span>{item.externalId}</span>
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    </td>
                    <td className="py-3">
                      <span className="px-2 py-1 bg-gray-700 text-gray-300 rounded-full text-xs capitalize">
                        {item.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-8">
            <BarChart3 className="w-8 h-8 text-gray-500 mx-auto mb-2" />
            <p className="text-gray-400">No connected items yet</p>
            <p className="text-sm text-gray-500 mt-1">Items will appear here after syncing</p>
          </div>
        )}
      </div>

      {/* Sync History */}
      <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
        <h2 className="text-lg font-semibold text-white mb-4">Sync History</h2>
        
        {syncHistory.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-left text-xs text-gray-400 border-b border-gray-700">
                  <th className="pb-2 font-medium">Date & Time</th>
                  <th className="pb-2 font-medium">Status</th>
                  <th className="pb-2 font-medium">Items Synced</th>
                  <th className="pb-2 font-medium">Duration</th>
                  <th className="pb-2 font-medium">Details</th>
                </tr>
              </thead>
              <tbody>
                {syncHistory.map((sync) => (
                  <tr key={sync.id} className="border-b border-gray-700">
                    <td className="py-3 text-white">
                      {new Date(sync.timestamp).toLocaleString()}
                    </td>
                    <td className="py-3">{getStatusBadge(sync.status)}</td>
                    <td className="py-3 text-white">{sync.items}</td>
                    <td className="py-3 text-white">{sync.duration}</td>
                    <td className="py-3">
                      {sync.error ? (
                        <span className="text-red-400 text-sm">{sync.error}</span>
                      ) : (
                        <button className="text-indigo-400 hover:text-indigo-300 text-sm">
                          View Details
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-8">
            <Clock className="w-8 h-8 text-gray-500 mx-auto mb-2" />
            <p className="text-gray-400">No sync history yet</p>
            <p className="text-sm text-gray-500 mt-1">Sync history will appear here after your first sync</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default IntegrationDetails;